import { z } from "zod";
import type { HubSpotCompany } from "./types";

export interface HubSpotCompanySummary {
  id: string;
  displayName: string;
  name: string | null;
  legalName: string | null;
  domain: string | null;
  lifecycleStage: string | null;
  risk: string | null;
  ownerId: string | null;
}

export interface HubSpotCompanyDetailV2 extends HubSpotCompanySummary {
  taxId: string | null;
  address: string | null;
  fiscalAddress: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  municipality: string | null;
  province: string | null;
  country: string | null;
  website: string | null;
  phone: string | null;
  description: string | null;
  industryCustom: string | null;
  industryStandard: string | null;
  employeeCount: string | null;
  annualRevenue: string | null;
  riskEtt: string | null;
  riskEttAmount: string | null;
  riskServices: string | null;
  riskServicesAmount: string | null;
  accountingNumber: string | null;
  tier: string | null;
  swift: string | null;
  iban: string | null;
  linkedInCompanyPage: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface HubSpotCompanySearchPage {
  results: HubSpotCompanySummary[];
  nextCursor: string | null;
}

export interface HubSpotCompanyNet4References {
  id: string;
  net4No3001Id: string | null;
  net4No3002Id: string | null;
}

export type HubSpotNameDuplicateCheckType = "name" | "razon_social";
interface HubSpotDuplicateCheckRequestBase {
  value: string;
  excludeCompanyIds?: string[];
}

export type HubSpotDuplicateCheckRequest =
  | (HubSpotDuplicateCheckRequestBase & { type: "name" })
  | (HubSpotDuplicateCheckRequestBase & { type: "razon_social" })
  | (HubSpotDuplicateCheckRequestBase & { type: "cif" })
  | (HubSpotDuplicateCheckRequestBase & { type: "domain" });

interface HubSpotDuplicateMatchBase {
  id: string;
  name: string | null;
  legalName: string | null;
  domain: string | null;
}

interface HubSpotNameDuplicateMatch extends HubSpotDuplicateMatchBase {
  similarity: number;
}

interface HubSpotCifDuplicateMatch extends HubSpotDuplicateMatchBase {
  taxId: string | null;
}

interface HubSpotNameDuplicateResult {
  type: "name";
  exists: boolean;
  matches: HubSpotNameDuplicateMatch[];
  similarMatches: HubSpotNameDuplicateMatch[];
}

interface HubSpotRazonSocialDuplicateResult {
  type: "razon_social";
  exists: boolean;
  matches: HubSpotNameDuplicateMatch[];
  similarMatches: HubSpotNameDuplicateMatch[];
}

interface HubSpotCifDuplicateResult {
  type: "cif";
  exists: boolean;
  matches: HubSpotCifDuplicateMatch[];
}

interface HubSpotDomainDuplicateResult {
  type: "domain";
  exists: boolean;
  matches: HubSpotDuplicateMatchBase[];
}

export type HubSpotDuplicateCheckResult =
  | HubSpotNameDuplicateResult
  | HubSpotRazonSocialDuplicateResult
  | HubSpotCifDuplicateResult
  | HubSpotDomainDuplicateResult;

const externalPayloadSchema = z.unknown();
type ExternalPayload = z.input<typeof externalPayloadSchema>;

const nullableString = (maxLength: number) => z.string().max(maxLength).nullable();
const requiredString = (maxLength: number) =>
  z.string().max(maxLength).refine((value) => value.trim().length > 0);
const companyIdSchema = z.string().regex(/^[0-9]{1,64}$/);
const dateTimeSchema = z.string().max(64).refine((value) => Number.isFinite(Date.parse(value)));

const companySummarySchema = z.strictObject({
  id: companyIdSchema,
  displayName: requiredString(500),
  name: nullableString(500),
  legalName: nullableString(500),
  domain: nullableString(253),
  lifecycleStage: nullableString(128),
  risk: nullableString(128),
  ownerId: nullableString(64),
});

const companyDetailV2Schema = companySummarySchema.extend({
  taxId: nullableString(64),
  address: nullableString(1000),
  fiscalAddress: nullableString(1000),
  city: nullableString(256),
  state: nullableString(256),
  postalCode: nullableString(64),
  municipality: nullableString(256),
  province: nullableString(256),
  country: nullableString(256),
  website: nullableString(2048),
  phone: nullableString(128),
  description: nullableString(10_000),
  industryCustom: nullableString(256),
  industryStandard: nullableString(256),
  employeeCount: nullableString(64),
  annualRevenue: nullableString(64),
  riskEtt: nullableString(128),
  riskEttAmount: nullableString(128),
  riskServices: nullableString(128),
  riskServicesAmount: nullableString(128),
  accountingNumber: nullableString(128),
  tier: nullableString(128),
  swift: nullableString(64),
  iban: nullableString(64),
  linkedInCompanyPage: nullableString(2048),
  createdAt: dateTimeSchema.nullable(),
  updatedAt: dateTimeSchema.nullable(),
});

const companySearchPageSchema = z.strictObject({
  results: z.array(companySummarySchema).max(100),
  nextCursor: nullableString(1024),
});

const companyNet4ReferencesSchema = z.strictObject({
  id: companyIdSchema,
  net4No3001Id: nullableString(64),
  net4No3002Id: nullableString(64),
});

export function parseHubSpotCompanyNet4References(value: ExternalPayload): HubSpotCompanyNet4References | null {
  const parsed = companyNet4ReferencesSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

const duplicateMatchBaseSchema = z.strictObject({
  id: companyIdSchema,
  name: nullableString(500),
  legalName: nullableString(500),
  domain: nullableString(253),
});

const nameDuplicateMatchSchema = duplicateMatchBaseSchema.extend({
  similarity: z.number().min(0).max(1),
});

const consistentMatches = <T extends z.ZodType>(matchSchema: T, maxMatches: number) =>
  z.strictObject({
    exists: z.boolean(),
    matches: z.array(matchSchema).max(maxMatches),
  }).superRefine((value, context) => {
    if (value.exists !== (value.matches.length > 0)) {
      context.addIssue({ code: "custom", message: "exists must match the matches collection" });
    }
  });

const nameDuplicateResultSchema = consistentMatches(nameDuplicateMatchSchema, 100).extend({
  type: z.literal("name"),
  similarMatches: z.array(nameDuplicateMatchSchema).max(8),
});

const razonSocialDuplicateResultSchema = consistentMatches(nameDuplicateMatchSchema, 100).extend({
  type: z.literal("razon_social"),
  similarMatches: z.array(nameDuplicateMatchSchema).max(8),
});

const cifDuplicateResultSchema = consistentMatches(
  duplicateMatchBaseSchema.extend({ taxId: nullableString(64) }),
  10,
).extend({ type: z.literal("cif") });

const domainDuplicateResultSchema = consistentMatches(duplicateMatchBaseSchema, 10).extend({
  type: z.literal("domain"),
});

const duplicateCheckResultSchema = z.union([
  nameDuplicateResultSchema,
  razonSocialDuplicateResultSchema,
  cifDuplicateResultSchema,
  domainDuplicateResultSchema,
]);

const problemDetailsSchema = z.strictObject({
  type: z.string().min(1).max(2048),
  title: z.string().max(160),
  status: z.number().int().min(400).max(599),
  detail: z.string().max(500),
  code: z.string().regex(/^[A-Z0-9_]+$/).max(80),
  correlationId: z.string().min(1).max(128),
});

const legacyCompanySchema = z.strictObject({
  id: companyIdSchema,
  displayName: z.string().optional(),
  properties: z.record(z.string(), z.string().nullable().optional().transform((value) => value ?? undefined)),
  createdAt: dateTimeSchema,
  updatedAt: dateTimeSchema,
  archived: z.boolean(),
  url: z.string().url().max(2048).optional(),
});

const companiesBatchReadResponseSchema = z.strictObject({
  completedAt: dateTimeSchema,
  results: z.array(legacyCompanySchema),
  startedAt: dateTimeSchema,
  status: z.literal("COMPLETE"),
  links: z.record(z.string(), z.string()).optional(),
  requestedAt: dateTimeSchema.optional(),
});

export function parseHubSpotCompanyDetailV2(value: ExternalPayload): HubSpotCompanyDetailV2 | null {
  const parsed = companyDetailV2Schema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function parseHubSpotCompanySearchPage(value: ExternalPayload): HubSpotCompanySearchPage | null {
  const parsed = companySearchPageSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

function parseHubSpotDuplicateCheckResult(
  value: ExternalPayload,
): HubSpotDuplicateCheckResult | null {
  const parsed = duplicateCheckResultSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function isHubSpotDuplicateCheckResult(value: ExternalPayload): value is HubSpotDuplicateCheckResult {
  return parseHubSpotDuplicateCheckResult(value) !== null;
}

export function parseHubSpotProblemDetails(value: ExternalPayload) {
  const parsed = problemDetailsSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function parseHubSpotCompaniesBatchReadResponse(value: ExternalPayload):
  { results: HubSpotCompany[] } | null {
  const parsed = companiesBatchReadResponseSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

const defined = (value: string | null): string | undefined => value ?? undefined;

export function toLegacyHubSpotCompany(
  company: HubSpotCompanySummary | HubSpotCompanyDetailV2,
): HubSpotCompany {
  const detail = "taxId" in company ? company : null;
  return {
    id: company.id,
    displayName: company.displayName,
    properties: {
      name: defined(company.name),
      razon_social: defined(company.legalName),
      domain: defined(company.domain),
      lifecyclestage: defined(company.lifecycleStage),
      riesgo: defined(company.risk),
      hubspot_owner_id: defined(company.ownerId),
      cif: detail ? defined(detail.taxId) : undefined,
      address: detail ? defined(detail.address) : undefined,
      direccion_fiscal: detail ? defined(detail.fiscalAddress) : undefined,
      city: detail ? defined(detail.city) : undefined,
      state: detail ? defined(detail.state) : undefined,
      zip: detail ? defined(detail.postalCode) : undefined,
      municipio: detail ? defined(detail.municipality) : undefined,
      provincia: detail ? defined(detail.province) : undefined,
      country: detail ? defined(detail.country) : undefined,
      website: detail ? defined(detail.website) : undefined,
      phone: detail ? defined(detail.phone) : undefined,
      description: detail ? defined(detail.description) : undefined,
      industria: detail ? defined(detail.industryCustom) : undefined,
      industry: detail ? defined(detail.industryStandard) : undefined,
      numberofemployees: detail ? defined(detail.employeeCount) : undefined,
      annualrevenue: detail ? defined(detail.annualRevenue) : undefined,
      riesgo_ett: detail ? defined(detail.riskEtt) : undefined,
      importe_riesgo_ett: detail ? defined(detail.riskEttAmount) : undefined,
      riesgo_servicios: detail ? defined(detail.riskServices) : undefined,
      importe_riesgo_servicios: detail ? defined(detail.riskServicesAmount) : undefined,
      numero_contable: detail ? defined(detail.accountingNumber) : undefined,
      tier: detail ? defined(detail.tier) : undefined,
      swift: detail ? defined(detail.swift) : undefined,
      iban: detail ? defined(detail.iban) : undefined,
      linkedin_company_page: detail ? defined(detail.linkedInCompanyPage) : undefined,
    },
    createdAt: detail?.createdAt ?? "",
    updatedAt: detail?.updatedAt ?? "",
    archived: false,
  };
}
